/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import { useRcmTableData } from '../../../../hooks/useRcmTableData'
import { RcmTable } from '../../../rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../table/TableComponents'
import DateCell from '../../../shared/DateCell'
import useDocumentsListService from '../../../../hooks/useDocumentsListService'
import TableMenu from './TableMenu.js'
import ValidDocIndicator from '../../../shared/ValidDocIndicator'
import DocIcon from '../../../shared/DocIcon'
import { AclLevel, FsiType } from '../../../../constants/fsi'
import { Link } from 'react-router'

interface PeriodicReviewItem {
  id: string
  name: string
  type: string
  acl: string
  valid: boolean
  nextPeriodicReviewDate: string
  workflow?: Array<{
    id: string
    formattedId: string
  }>
  systemFolderType?: string
}

export const UpcomingPeriodicReviewTable: React.FC = () => {
  const {
    service: {
      filter: { translate }
    }
  } = useServiceContext()
  const tableData = useRcmTableData()
  const { getFsiNameTooltip } = useDocumentsListService()

  useEffect(() => {
    tableData.refreshTable()

    // Listen for global dashboard update events
    const handleUpdateDashboard = () => {
      tableData.refreshTable()
    }
    window.addEventListener('updateDashboard', handleUpdateDashboard)

    return () => {
      window.removeEventListener('updateDashboard', handleUpdateDashboard)
    }
  }, []) // Empty dependency array to run only on mount

  return (
    <RcmTable
      className="chita-table fsiTable dashboard-table"
      noResultsText="NO_RESULTS_FILTERED"
      hidePaginate={true}
      disableOnInitLoad={false}
      TableFooter={
        <tr>
          <td colSpan={4} className="view-all">
            <Link to="/documents/periodic-review">
              {translate('VIEW_ALL')} ({tableData.total > 500 ? '500+' : tableData.total})
            </Link>
          </td>
        </tr>
      }
      HeadRow={
        <TableHeadRow>
          <TableHeadCell id="bfeb8edd-f32f-49fd-ac51-42df4796f278">{translate('NAME')}</TableHeadCell>
          <TableHeadCell id="fc8378ba-ce1d-4048-8e3c-04e4dded3d5e">{translate('CHANGE_REQUEST')}</TableHeadCell>
          <TableHeadCell id="b5d1538f-df52-410c-8bac-16bb4d82bf08">
            {translate('NEXT_PERIODIC_REVIEW_DATE')}
          </TableHeadCell>
          <TableHeadCell id="535a6369-02b0-4f62-8d71-992695d03e23" className="actions">
            {translate('ACTIONS')}
          </TableHeadCell>
        </TableHeadRow>
      }
      Row={(props: { row: PeriodicReviewItem }) => {
        const { row } = props
        return (
          <TableRow {...props} key={row.id}>
            <td className="name clipped-cell with-thumb">
              <div className="d-flex align-left">
                {row.type !== FsiType.FOLDER && <ValidDocIndicator fsi={row} />}
                <DocIcon fsi={row} inWorkflow={row.workflow && row.workflow.length > 0} />
                <div className="ellipsis" title={getFsiNameTooltip(row)}>
                  {row.type === FsiType.PLACEHOLDER ||
                  (row.acl === AclLevel.BROWSE && row.type !== FsiType.FOLDER) ||
                  !row.valid ? (
                    <span>{row.name}</span>
                  ) : (
                    <Link
                      to={
                        row.type === FsiType.FOLDER
                          ? `/browser?folder=${row.id}&type=${row.systemFolderType}&page=1`
                          : `/show-file/${row.id}`
                      }
                    >
                      {row.name}
                    </Link>
                  )}
                </div>
              </div>
            </td>
            <td className="narrow">
              {row?.workflow?.[0]?.id && row?.workflow?.[0]?.formattedId ? (
                <button
                  type="button"
                  className="text-link-btn undefined btn btn-link"
                  onClick={() => {
                    // TODO-WON'T-FIX: Show workflow details modal when modals controller is available, Wont fix- as now we removed Overview/Home page
                    console.log('Show workflow details for:', row?.workflow?.[0]?.id)
                  }}
                >
                  {row?.workflow?.[0]?.formattedId}
                </button>
              ) : (
                translate('NOT_AVAILABLE')
              )}
            </td>
            <td className="narrow updated">
              <DateCell includeTime={false} val={row.nextPeriodicReviewDate} />
            </td>
            <td className="actions">{row.valid && <TableMenu item={row} />}</td>
          </TableRow>
        )
      }}
    />
  )
}
